import axios from "axios";
import moment from "moment";

import { getDeviceTypeList } from "../../telemetry-utils";
import { APP_BASE_URL, GET_ASSET_FORM, GET_COLLECTION_MAP_ID } from "../../../../Common/EndPoints";

export async function getDeviceTypeForLabel(type: string) {
  const device_type_list = await getDeviceTypeList();
  const pm_gear_type = device_type_list.data.data.find((device) => device.type === type);
  if (!pm_gear_type) {
    throw new Error("PM_GEAR_TYPE_NOT_FOUND");
  }

  return pm_gear_type;
  // const pm_gear_typeid = pm_gear_type.typeid;
}
const POINT_MACHINE_INFO_FORM_TO_KEY = {
  end: "6ef56875-194d-4401-b05d-550ff9b82a44",
  type: "b8df57ca-74a0-45f7-9f12-5df9d4d638bb",
  connected: "c7ab33fb-c251-4d52-97e9-8efb4116edc7",
};

export async function getPointMachineInformation(device_id: string, pm_gear_typeid: string) {
  const collection_map_url = `${APP_BASE_URL}${GET_COLLECTION_MAP_ID}?type_id=${pm_gear_typeid}`;
  const collection_map = await axios.get(collection_map_url);
  const collection_map_id = collection_map?.data?.[0].collectionmapid;

  const form_url = `${APP_BASE_URL}${GET_ASSET_FORM}?collection_map_id=${collection_map_id}&with_responses=true&device_id=${device_id}`;
  const form_list_raw = await axios.get(form_url);
  const form_list = form_list_raw.data.data;

  for (const form of form_list) {
    if (form.name === "POINT_MACHINE_INFORMATION") {
      const data = form.responses[0].data;
      return {
        end: data[POINT_MACHINE_INFO_FORM_TO_KEY.end],
        type: data[POINT_MACHINE_INFO_FORM_TO_KEY.type],
        connected: data[POINT_MACHINE_INFO_FORM_TO_KEY.connected],
      };
    }
  }

  throw new Error("Information not available for this Point Machine");
}

type PacketData = Record<string, [{ ts: string; value: string }]>;
type ParsedData = Record<string, PacketData>;
type PopulateLogicType = { divide_by?: number; unit?: string; show_time_on_hover?: boolean };

function populateSeriesWireForPM(element: HTMLElement, data: ParsedData, key: string) {
  function _isValuePresent(_key: string) {
    return Object.values(data).every((data) => {
      const value = data[_key][0].value;
      if (!value || value === "0" || value.includes("*") || isNaN(Number(value)) || Number(value) < 18000) return false;
      return true;
    });
  }

  function _colorLine(color: string) {
    element.style.stroke = color;
    element.style.strokeWidth = "3px";
  }

  const nwkr_present = _isValuePresent("NWKR");
  const rwkr_present = _isValuePresent("RWKR");
  const rnwkr_present = _isValuePresent("RNWKR");
  const rrwkr_present = _isValuePresent("RRWKR");

  if (!nwkr_present && !rwkr_present) {
    _colorLine("red");
    return;
  }

  switch (key) {
    case "NWKR": {
      if (nwkr_present) {
        _colorLine("green");
      } else {
        _colorLine("gray");
      }
      break;
    }
    case "RWKR": {
      if (rwkr_present) {
        _colorLine("green");
      } else {
        _colorLine("gray");
      }
      break;
    }
    case "RRWKR": {
      if (!rrwkr_present && !rnwkr_present) {
        _colorLine("red");
        break;
      }
      if (rrwkr_present) {
        _colorLine("green");
      } else {
        _colorLine("gray");
      }
      break;
    }
    case "RNWKR": {
      if (!rrwkr_present && !rnwkr_present) {
        _colorLine("red");
        break;
      }
      if (rnwkr_present) {
        _colorLine("green");
      } else {
        _colorLine("gray");
      }
      break;
    }
    default: {
    }
  }
}
function populateDateTimeForPM(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const device_key_timestamp = data?.[key]?.[0]?.ts;
  if (!device_key_timestamp || isNaN(Number(device_key_timestamp))) return;
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = moment(Number(device_key_timestamp)).format("DD-MM-YYYY HH:mm:ss");
}
function populateOperationLineWithOT(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const ot_val = data?.[key]?.[0]?.value;
  if (!ot_val) return;
  const numerical_ot_val = Number(ot_val);

  if (!numerical_ot_val) return;
  if (numerical_ot_val < 10000) {
    element.style.stroke = "green";
  } else {
    element.style.stroke = "red";
  }
}
function populateText(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const value = data?.[key]?.[0]?.value;

  const { divide_by = 1, unit = "", show_time_on_hover } = config;
  const numerical_value = Number(value);
  if (!value) return;
  let divided_value;
  if (numerical_value && !isNaN(numerical_value)) {
    divided_value = numerical_value / divide_by + " " + unit;
  } else {
    divided_value = value;
  }
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = divided_value;

  if (show_time_on_hover) {
    showTimeOnHover(element, data, key, config);
  }
}
function showTimeOnHover(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const timestamp = data[key][0].ts;
  if (!timestamp) return;
  const formatted_date_time = moment(Number(timestamp)).format("DD-MM-YYYY HH:mm:ss");
  var titleElement = document.createElementNS("http://www.w3.org/2000/svg", "title");
  titleElement.textContent = `Last Time Captured: ${formatted_date_time}`;
  element.style.cursor = "pointer";
  element.appendChild(titleElement);
}

function _isValuePresent(data: ParsedData | PacketData, _key: string) {
  return Object.values(data).some((data) => {
    const value = data?.[_key]?.[0]?.value;
    if (!value || value === "0" || value.includes("*") || isNaN(Number(value)) || Number(value) < 18000) return false;
    return true;
  });
}

function populateParallelWireForPM(element: HTMLElement, data: ParsedData, key: string, config: any) {
  // console.log("element_data", data, config);
  function _colorLine(color: string) {
    element.style.stroke = color;
    element.style.strokeWidth = "3px";
  }

  if (config.primary_key) {
    const isPrimaryKeyPresent = _isValuePresent(data, config.primary_key);
    if (isPrimaryKeyPresent) {
      _colorLine("green");
      return;
    }
  }

  if (config.secondary_key && config.device_id_to_sub_check) {
    const device_data = data[config.device_id_to_sub_check];
    const isSecondaryKeyPresent = _isValuePresent(device_data, config.device_id_to_sub_check);
    if (isSecondaryKeyPresent) {
      _colorLine("green");
      return;
    }
  }
}

export function getParallelPointMachineSvgDataMap(
  data: Record<any, any>,
  device_id_of_pm_on_end_a: string,
  device_id_of_pm_on_end_b?: string,
) {
  if (!device_id_of_pm_on_end_a && !device_id_of_pm_on_end_b) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_A_AND_B", data);
    return {};
  }

  const COMMON_POINT_MACHINE_CONFIG = {
    B24_PointA_RWKR_WIRE_STATUS: {
      primary_key: "RRWKR",
      secondary_key: "RWKR",
      device_id_to_sub_check: device_id_of_pm_on_end_b,
      populate_element: populateParallelWireForPM,
    },
    B24_PointB_RWKR_WIRE_STATUS: {
      primary_key: "RRWKR",
      secondary_key: "RWKR",
      device_id_to_sub_check: device_id_of_pm_on_end_a,
      populate_element: populateParallelWireForPM,
    },
    B24N24_RWKR_LINE: {
      primary_key: "RRWKR",
      populate_element: populateParallelWireForPM,
    },
    N24_RWKR_WIRE_STATUS: {
      primary_key: "RRWKR",
      populate_element: populateParallelWireForPM,
    },
    B24_PointA_RWKR_WIRE_STATUS_2: {
      primary_key: "RNWKR",
      secondary_key: "NWKR",
      device_id_to_sub_check: device_id_of_pm_on_end_b,
      populate_element: populateParallelWireForPM,
    },
    B24_PointB_RWKR_WIRE_STATUS_2: {
      primary_key: "RNWKR",
      secondary_key: "NWKR",
      device_id_to_sub_check: device_id_of_pm_on_end_a,
      populate_element: populateParallelWireForPM,
    },
    B24N24_NWKR_LINE: {
      primary_key: "RNWKR",
      populate_element: populateParallelWireForPM,
    },
    N24_RWKR_WIRE_STATUS_2: {
      primary_key: "RNWKR",
      populate_element: populateParallelWireForPM,
    },
  };
  const POINT_MACHINE_A_CONFIG = {
    RW_PointA: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    NW_PointA: {
      key: "VRO",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    CW_PointA: {
      key: "OC",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
  };

  if (!device_id_of_pm_on_end_b) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_B", data);
    return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_A_CONFIG };
  }
  const POINT_MACHINE_B_CONFIG = {
    RW_PointB: {
      key: "VRO",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    NW_PointB: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    CW_PointB: {
      key: "OC",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
  };
  if (!device_id_of_pm_on_end_a) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_A", data);
    return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_B_CONFIG };
  }

  return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_A_CONFIG, ...POINT_MACHINE_B_CONFIG };
}

export function getSingleEndPointMachineSvgDataMap(data: Record<any, any>, device_id: string) {
  if (!device_id) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_SINGLE_END_PM", data);
    return {};
  }

  const config = {
    B24_PointA_RWKR_WIRE_STATUS: {
      primary_key: "RRWKR",
      secondary_key: "RWKR",
      populate_element: populateParallelWireForPM,
    },
    B24N24_RWKR_LINE: {
      primary_key: "RRWKR",
      secondary_key: "RWKR",
      populate_element: populateParallelWireForPM,
    },
    N24_RWKR_WIRE_STATUS: {
      primary_key: "RRWKR",
      secondary_key: "RWKR",
      populate_element: populateParallelWireForPM,
    },
    B24_PointB_RWKR_WIRE_STATUS: {
      primary_key: "RNWKR",
      secondary_key: "NWKR",
      populate_element: populateParallelWireForPM,
    },
    B24N24_NWKR_LINE: {
      primary_key: "RNWKR",
      secondary_key: "NWKR",
      populate_element: populateParallelWireForPM,
    },
    N24_RWKR_WIRE_STATUS_2: {
      primary_key: "RNWKR",
      secondary_key: "NWKR",
      populate_element: populateParallelWireForPM,
    },
    RW_PointA: {
      key: "VNO",
      device_id,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    NW_PointA: {
      key: "VRO",
      device_id,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    CW_PointA: {
      key: "OC",
      device_id,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
  };

  return config;
}

export function getSeriesPointMachineSvgDataMap(
  data: Record<any, any>,
  device_id_of_pm_on_end_a: string,
  device_id_of_pm_on_end_b: string,
) {
  if (!device_id_of_pm_on_end_a && !device_id_of_pm_on_end_b) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_A_AND_B", data);
    return {};
  }

  const COMMON_POINT_MACHINE_CONFIG = {
    Outdoor_Detection_NWKR_Wire1: {
      key: "NWKR",
      populate_element: populateSeriesWireForPM,
    },
    NWKR_DETECTION1: {
      key: "NWKR",
      populate_element: populateSeriesWireForPM,
    },
    NWKR_DETECTION2: {
      key: "NWKR",
      populate_element: populateSeriesWireForPM,
    },
    RWKR_DETECTION1: {
      key: "RWKR",
      populate_element: populateSeriesWireForPM,
    },
    RWKR_DETECTION2: {
      key: "RWKR",
      populate_element: populateSeriesWireForPM,
    },
    Outdoor_Detection_NWKR_Wire2: {
      key: "NWKR",
      populate_element: populateSeriesWireForPM,
    },
    Outdoor_Detection_NWKR_Wire3: {
      key: "NWKR",
      populate_element: populateSeriesWireForPM,
    },
    Outdoor_Detection_RWKR_Wire1: {
      key: "RWKR",
      populate_element: populateSeriesWireForPM,
    },
    Outdoor_Detection_RWKR_Wire2: {
      key: "RWKR",
      populate_element: populateSeriesWireForPM,
    },
    Outdoor_Detection_RWKR_Wire3: {
      key: "RWKR",
      populate_element: populateSeriesWireForPM,
    },
    Indoor_Detection_RNWKR_Wire1: {
      key: "RNWKR",
      populate_element: populateSeriesWireForPM,
    },
    Indoor_Detection_RNWKR_Wire2: {
      key: "RNWKR",
      populate_element: populateSeriesWireForPM,
    },
    Indoor_Detection_RRWKR_Wire1: {
      key: "RRWKR",
      populate_element: populateSeriesWireForPM,
    },
    Indoor_Detection_RRWKR_Wire2: {
      key: "RRWKR",
      populate_element: populateSeriesWireForPM,
    },
    Indoor_Detection_RRWKR_Wire3: {
      key: "RRWKR",
      populate_element: populateSeriesWireForPM,
    },
  };

  const POINT_MACHINE_A_CONFIG = {
    PointA_Operation_Date_Time: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateDateTimeForPM,
    },
    PointA_Outdoor_Detection_Date_Time: {
      key: "NWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateDateTimeForPM,
    },
    PointA_Indoor_Detection_Date_Time: {
      key: "RNWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateDateTimeForPM,
    },
    Point2_Operation_Name: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    PointA_Detection_Outdoor: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    Point2_Operation_Name_Box_Text: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    PointA_Operation: {
      key: "OT",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateOperationLineWithOT,
    },
    PM_A_DIR: {
      key: "DIR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    PointA_Detection_Indoor: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    PM_A_VNO_Voltage: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_A_VRO_Voltage: {
      key: "VRO",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_A_PC_Current: {
      key: "PC",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
    PM_A_OC_Current: {
      key: "OC",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
    LOCATION_BOX: {
      key: "LOC",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
    },
    PM_A_NWKR_Voltage_Outdoor: {
      key: "NWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_A_RWKR_Voltage_Outdoor: {
      key: "RWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_A_RNWKR_Voltage_Indoor: {
      key: "RNWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_A_RRWKR_Voltage_Indoor: {
      key: "RRWKR",
      device_id: device_id_of_pm_on_end_a,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
  };

  if (!device_id_of_pm_on_end_b) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_B", data);
    return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_A_CONFIG };
  }

  const POINT_MACHINE_B_CONFIG = {
    PointB_Operation_Date_Time: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateDateTimeForPM,
    },
    PointB_Outdoor_Detection_Date_Time: {
      key: "NWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateDateTimeForPM,
    },
    PointB_Indoor_Detection_Date_Time: {
      key: "RNWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateDateTimeForPM,
    },
    Point1_Operation_Name: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    PointB_Detection_Outdoor: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    Point2_Operation_Name_Box_Text_2: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    PointB_Operation: {
      key: "OT",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateOperationLineWithOT,
    },
    PM_B_DIR: {
      key: "DIR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    PointB_Detection_Indoor: {
      key: "NAME",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    LOCATION_BOX: {
      key: "LOC",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
    },
    PM_B_VNO_Voltage: {
      key: "VNO",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_B_VRO_Voltage: {
      key: "VRO",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_B_PC_Current: {
      key: "PC",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },
    PM_B_OC_Current: {
      key: "OC",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "A",
    },

    PM_B_NWKR_Voltage_Outdoor: {
      key: "NWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_B_RWKR_Voltage_Outdoor: {
      key: "RWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },

    PM_B_RNWKR_Voltage_Indoor: {
      key: "RNWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
    PM_B_RRWKR_Voltage_Indoor: {
      key: "RRWKR",
      device_id: device_id_of_pm_on_end_b,
      populate_element: populateText,
      divide_by: 1000,
      unit: "V",
    },
  };

  if (!device_id_of_pm_on_end_a) {
    console.error("POINT_MACHINE_CONFIGURATION_NOT_FOUND_FOR_A", data);
    return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_B_CONFIG };
  }

  return { ...COMMON_POINT_MACHINE_CONFIG, ...POINT_MACHINE_A_CONFIG, ...POINT_MACHINE_B_CONFIG };
}
